import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import remarkRehype from 'remark-rehype';
import { toHtml } from 'hast-util-to-html';
import type { Nodes } from 'hast';

const processor = unified().use(remarkParse).use(remarkGfm).use(remarkRehype).freeze();

const breaksProcessor = unified()
  .use(remarkParse)
  .use(remarkGfm)
  .use(remarkBreaks)
  .use(remarkRehype)
  .freeze();

/**
 * Markdown -> HTML for the clipboard's `text/html` flavor.
 *
 * Slack, Google Docs, Gmail, and Notion all paste rich text when the
 * clipboard carries `text/html`; with only `text/plain` they show the raw
 * markdown source. This runs the same remark pipeline `MarkdownContent`
 * renders with, so what you paste matches what you saw on screen.
 *
 * Raw HTML embedded in the markdown is dropped: `remark-rehype` ignores raw
 * nodes unless `allowDangerousHtml` is set, which keeps agent-authored text
 * from smuggling markup into another app's document.
 */
export function markdownToClipboardHtml(
  markdown: string,
  { preserveLineBreaks = false }: { preserveLineBreaks?: boolean } = {},
): string {
  if (!markdown.trim()) return '';

  const active = preserveLineBreaks ? breaksProcessor : processor;
  const hast = active.runSync(active.parse(markdown)) as Nodes;
  return toHtml(hast);
}

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
};

/** Escapes text destined for the clipboard's HTML flavor as literal content. */
export function escapeClipboardHtml(text: string): string {
  return text.replace(/[&<>"]/g, (char) => HTML_ESCAPES[char] ?? char);
}

/** Wraps preformatted text (tool payloads, JSON) as a pasteable code block. */
export function preformattedClipboardHtml(text: string): string {
  return `<pre><code>${escapeClipboardHtml(text)}</code></pre>`;
}
